import { useEffect, useMemo, useState, type ReactNode, useRef } from 'react';
import { arrangeDtCols } from '@/shared/components/docStatusCols';
import uiPrefs from '@/store/uiPrefs';
import ColumnSettingsButton, {
  applyColPrefs, clearColPrefs, loadColPrefs, saveColPrefs, subscribeColPrefs,
  type ColItem, type ColPrefs,
} from '@/shared/components/ColumnSettings';
import ImportedBadge from './ImportedBadge';

/**
 * Спільна таблиця даних у стилі сторінки «Співробітники» (компактна HTML-таблиця).
 *
 * Фічі (єдині для всіх сторінок сайту):
 *  - ресайз колонок (тягни межу заголовка);
 *  - сортування по кліку на заголовок (▲/▼, ↕ для несортованих);
 *  - значок «І» імпорту з іншої бази (колонка з importBadge: true показує бейдж
 *    перед значенням за полем external_source рядка);
 *  - double-click по рядку → onRowDoubleClick;
 *  - приглушення рядків (rowMuted): напр. не чинні / на видаленні.
 *
 * Колонки задаються конфігом ColumnDef. value(row) повертає вміст комірки
 * (ReactNode); sortValue(row) — значення для сортування (за замовч. value як рядок).
 */

export interface ColumnDef<T> {
  key: string;
  header: ReactNode;
  /** Вміст комірки. */
  cell: (row: T) => ReactNode;
  /** Значення для сортування (число/рядок). За замовч. — текстовий cell недоступний,
   *  тож для сортованих колонок задавай sortValue. */
  sortValue?: (row: T) => string | number;
  /** Початкова ширина, px (за замовч. auto/flex). */
  width?: number;
  align?: 'left' | 'center' | 'right';
  /** Показати бейдж імпорту перед вмістом (бере row.external_source). */
  importBadge?: boolean;
}

interface Props<T> {
  rows: T[];
  columns: ColumnDef<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyText?: string;
  onRowDoubleClick?: (row: T) => void;
  /** Приглушити рядок (opacity): напр. не чинний/на видаленні. */
  rowMuted?: (row: T) => number | undefined;
  /** Фон рядка: напр. контроль підписання (0429) — напівпрозорий червоний
   *  для непідписаної первинки. */
  rowBackground?: (row: T) => string | undefined;
  /** Жирний рядок: напр. непереглянуті документи (Кабінет ДПС, 0432). */
  rowBold?: (row: T) => boolean;
  /** Поле external_source для importBadge (за замовч. 'external_source'). */
  importField?: string;
  /** Початкове сортування. */
  initialSortKey?: string;
  initialSortDir?: 'asc' | 'desc';
  /** Ключ для збереження ширин колонок у localStorage (per-table). Якщо не
   *  заданий — обчислюється автоматично з URL + складу колонок (стабільно
   *  розрізняє таблиці без правок усіх сторінок). */
  storageKey?: string;
}

/** Мінімум для колонки без заданої ширини (вона тягнеться, але не зникає) — для суми
 *  ширин таблиці, яку отримує Firefox замість `max-content` (styles.css, --dg-min).
 *  80, а не більше: Chromium дає такій колонці ширину її вмісту, і з більшим мінімумом
 *  таблиця у Firefox прокручувалась би там, де в Chromium уміщається («Організації» на
 *  екрані 1280: задані ширини 880 з 970). */
export const AUTO_COL_MIN = 80;

// Префікс ключа localStorage для ширин колонок DataTable.
const WIDTHS_LS_PREFIX = 'dt-widths:';
// ч.1332: префікс ключа обраного сортування (профіль користувача, родина uiPrefs `dt-sort:`).
const SORT_LS_PREFIX = 'dt-sort:';

function readSort(key: string): { key: string; dir: 'asc' | 'desc' } | null {
  try {
    const v = uiPrefs.get<{ key: string; dir: 'asc' | 'desc' }>(key);
    return v && typeof v.key === 'string' && (v.dir === 'asc' || v.dir === 'desc') ? v : null;
  } catch { return null; }
}

export default function DataTable<T>({
  rows, columns: rawColumns, rowKey, loading, emptyText = 'Немає даних.',
  onRowDoubleClick, rowMuted, rowBackground, rowBold, importField = 'external_source',
  initialSortKey, initialSortDir = 'asc', storageKey,
}: Props<T>) {
  // Статус-колонки (проведено/вкладення/підпис) — іконки + канонічний порядок
  // одразу після бейджа міграції (рішення власника 2026-08-23). Це ДЕФОЛТ;
  // поверх — користувацькі налаштування порядку/показу (2026-08-26).
  const baseColumns = useMemo(() => arrangeDtCols(rawColumns), [rawColumns]);
  const prefsKey = storageKey
    ?? `${typeof window !== 'undefined' ? window.location.pathname : ''}|${baseColumns.map((c) => c.key).join(',')}`;
  const [colPrefs, setColPrefs] = useState<ColPrefs | null>(() => loadColPrefs(prefsKey));
  useEffect(
    () => subscribeColPrefs(prefsKey, () => setColPrefs(loadColPrefs(prefsKey))),
    [prefsKey],
  );
  const columns = useMemo(
    () => applyColPrefs(baseColumns, colPrefs, (c) => c.key),
    [baseColumns, colPrefs],
  );
  const colItems: ColItem[] = useMemo(() => {
    const SPECIAL: Record<string, string> = {
      posted: 'Проведено', _att: 'Вкладення', sign_control: 'Підпис',
    };
    const ordered = applyColPrefs(
      baseColumns, colPrefs && { ...colPrefs, hidden: [] }, (c) => c.key);
    return ordered.map((c) => ({
      id: c.key,
      label: SPECIAL[c.key]
        ?? (typeof c.header === 'string' && c.header ? c.header : c.key),
    }));
  }, [baseColumns, colPrefs]);
  // ч.1332 (запит власника «зберігай сортування в колонках»): обране сортування — у профілі
  // користувача, як ширини й порядок колонок; «скинути» в налаштуваннях колонок повертає початкове.
  const sortPrefKey = SORT_LS_PREFIX + prefsKey;
  const [sortKey, setSortKey] = useState<string | null>(
    () => readSort(sortPrefKey)?.key ?? initialSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => readSort(sortPrefKey)?.dir ?? initialSortDir);
  // Профіль приходить із сервера ПІСЛЯ першого показу (інший компʼютер), а ключ таблиці
  // змінюється, коли колонки догружаються, — тоді сортування перечитується.
  useEffect(() => {
    const apply = () => {
      const v = readSort(sortPrefKey);
      if (v) { setSortKey(v.key); setSortDir(v.dir); }
    };
    apply();
    return uiPrefs.subscribe(sortPrefKey, apply);
  }, [sortPrefKey]);
  const persistSort = (key: string, dir: 'asc' | 'desc') => {
    try { uiPrefs.set(sortPrefKey, { key, dir }); } catch { /* приватний режим — не критично */ }
  };

  // Стабільний ключ персисту: явний storageKey АБО авто (шлях + ключі колонок).
  // Склад колонок розрізняє кілька таблиць на одній сторінці; шлях — між сторінками.
  const colSig = columns.map((c) => c.key).join(',');
  const lsKey = WIDTHS_LS_PREFIX + (
    storageKey ?? `${typeof window !== 'undefined' ? window.location.pathname : ''}|${colSig}`
  );

  // Ширини колонок (px) — змінюються ресайзом; ключ → width.
  // Iніціалізація: дефолтні width з колонок, поверх — збережені у localStorage
  // (тільки для колонок, що ще існують — нові беруть дефолт).
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const w: Record<string, number> = {};
    for (const c of columns) if (c.width) w[c.key] = c.width;
    try {
      const saved = uiPrefs.get<Record<string, number>>(lsKey);
      if (saved) {
        const valid = new Set(columns.map((c) => c.key));
        for (const [k, v] of Object.entries(saved)) {
          if (valid.has(k) && typeof v === 'number' && v >= 50) w[k] = v;
        }
      }
    } catch { /* пошкоджений запис / приватний режим — ігноруємо, беремо дефолти */ }
    return w;
  });

  // SUP-96DD97EE: сума ширин колонок — мінімальна ширина таблиці числом (Firefox не вміє
  // max-content для таблиці з фіксованою розкладкою; див. styles.css).
  const minTableWidth = columns.reduce((s, c) => s + (widths[c.key] ?? AUTO_COL_MIN), 0);

  // Зберегти поточні ширини у localStorage. Викликається у кінці ресайзу
  // (onUp), а не на кожен рух миші — менше записів, без засмічення на mount.
  const persistWidths = (w: Record<string, number>) => {
    try {
      uiPrefs.set(lsKey, w);
    } catch { /* quota / приватний режим — не критично */ }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a); const vb = col.sortValue!(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return 0;
    });
  }, [rows, sortKey, sortDir, columns]);

  const toggleSort = (c: ColumnDef<T>) => {
    if (!c.sortValue) return;
    const dir: 'asc' | 'desc' = sortKey === c.key ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortKey(c.key);
    setSortDir(dir);
    persistSort(c.key, dir);
  };

  // SUP-321707CF (ч.1239): таблиця ширша за екран — колонки праворуч (часто саме підсумкові:
  // «Сальдо», «Сума») ховаються за край, а смугу прокрутки помічають не всі. Показуємо
  // позначку «ще колонки →»; клік прокручує до кінця.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [moreRight, setMoreRight] = useState(false);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const check = () => setMoreRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 8);
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => { el.removeEventListener('scroll', check); ro?.disconnect(); };
  }, [columns.length, widths, rows.length]);

  const startResize = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = (e.currentTarget as HTMLElement).parentElement!;
    const startW = widths[key] ?? th.getBoundingClientRect().width;
    const onMove = (ev: MouseEvent) => {
      // не ширше 700: колонка на весь екран ховає всі інші
      const w = Math.min(700, Math.max(50, startW + ev.clientX - startX));
      setWidths((prev) => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Зберегти фінальні ширини після завершення drag (актуальний стан
      // дістаємо через функціональний setWidths, без stale-замикання).
      setWidths((cur) => { persistWidths(cur); return cur; });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, marginBottom: 2 }}>
      {moreRight && (
        <button type="button" className="data-grid__more"
          title="Таблиця ширша за екран: праворуч є ще колонки. Натисніть, щоб прокрутити до них"
          onClick={() => wrapRef.current?.scrollTo({ left: wrapRef.current.scrollWidth, behavior: 'smooth' })}>
          ще колонки →
        </button>
      )}
      <ColumnSettingsButton
        items={colItems}
        hidden={colPrefs?.hidden ?? []}
        onChange={(order, hidden) => {
          const next = { order, hidden };
          saveColPrefs(prefsKey, next);
          setColPrefs(next);
        }}
        onReset={() => {
          clearColPrefs(prefsKey); setColPrefs(null);
          // SUP-321707CF (ч.1239): «скинути» повертає і ШИРИНИ колонок. Розтягнута випадково
          // перша колонка виштовхує решту за край екрана, а іншого способу повернути їх не було.
          try { uiPrefs.remove(lsKey); uiPrefs.remove(sortPrefKey); } catch { /* приватний режим — не критично */ }
          setSortKey(initialSortKey ?? null);
          setSortDir(initialSortDir);
          const w: Record<string, number> = {};
          for (const c of baseColumns) if (c.width) w[c.key] = c.width;
          setWidths(w);
        }}
      />
    </div>
    {columns.length === 0 && (
      // ч.1242: приховали всі колонки — таблиця зникала без жодного пояснення.
      <div className="data-grid__msg" style={{ textAlign: 'left' }}>
        Усі колонки таблиці приховано. Натисніть шестерню праворуч і «скинути», щоб повернути їх.
      </div>
    )}
    <div className="data-grid-wrap" ref={wrapRef}>
    <table className="data-grid" style={{ ['--dg-min' as string]: `${minTableWidth}px` }}>
      <thead>
        <tr>
          {columns.map((c) => (
            <th
              key={c.key}
              style={{
                width: widths[c.key], textAlign: c.align ?? 'left',
                cursor: c.sortValue ? 'pointer' : 'default',
              }}
              onClick={() => toggleSort(c)}
            >
              <span className="data-grid__th-label">
                {c.header}
                {c.sortValue && (
                  <span className="data-grid__sort">
                    {sortKey === c.key ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                  </span>
                )}
              </span>
              <span className="data-grid__resizer"
                    onMouseDown={(e) => startResize(c.key, e)} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr><td colSpan={columns.length} className="data-grid__msg">Завантаження…</td></tr>
        ) : sorted.length === 0 ? (
          <tr><td colSpan={columns.length} className="data-grid__msg">{emptyText}</td></tr>
        ) : sorted.map((row) => (
          <tr key={rowKey(row)}
              style={{ opacity: rowMuted?.(row),
                background: rowBackground?.(row),
                fontWeight: rowBold?.(row) ? 700 : undefined,
                cursor: onRowDoubleClick ? 'pointer' : undefined }}
              onDoubleClick={() => onRowDoubleClick?.(row)}>
            {columns.map((c) => (
              <td key={c.key} style={{ textAlign: c.align ?? 'left' }}>
                {c.importBadge ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <ImportedBadge source={(row as Record<string, unknown>)[importField] as string | null} />
                    <span>{c.cell(row)}</span>
                  </span>
                ) : c.cell(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    </div>
    </div>
  );
}
